import React, { memo, useCallback, useMemo, useLayoutEffect, useRef, useState } from 'react';
import { View, Text, StyleSheet, Image, Platform } from 'react-native';
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  useDerivedValue,
  withSpring,
  withSequence,
  withTiming,
  cancelAnimation,
  interpolate,
  interpolateColor,
  Extrapolation,
  Easing,
  type SharedValue,
} from 'react-native-reanimated';
import { useAuthNavigation } from '../../shared/navigation/useAuthNavigation';
import { Clickable2, type Clickable2PressState } from '../../components/common/Clickable2';
import { SvgIcon } from '../../components/common/SvgIcon';
import { useTranslation, getApiText, formatNumber } from '../../shared/i18n';
import { useRtl } from '../../shared/i18n/useRtl';
import { horizontalLtrDirectionStyle } from '../../shared/i18n/rtl';
import { MenuItemData } from '../../data/domains/foodMenu';
import { resolveMenuItemScaledCarbsDisplay } from '../../data/domains/menu/scaledCarbs';
import { useNutritionUiEnabled } from '../../data/domains/config';
import { appTheme, type ThemeColors } from '../../shared/theme';
import { useScreenTheme } from '../../shared/theme/useScreenTheme';
import { DASH_STUDIO_RADIUS } from '../../shared/theme/studio/dashboardConstants';
import { triggerTactileHaptic, triggerMenuItemHaptic, resolveMenuFlowCardTap, type MenuItemHealthHaptic } from './tactile';
import {
  MEAL_SNACK_TAG_BORDER_RADIUS,
  MEAL_SNACK_TAG_EXPAND_BTN_RADIUS,
  MEAL_SNACK_TAG_EXPAND_BTN_SIZE,
  MEAL_SNACK_TAG_EXPAND_ICON_SIZE,
  MEAL_SNACK_TAG_LONG_HEIGHT,
  MEAL_SNACK_TAG_LONG_PADDING_H,
  MEAL_SNACK_TAG_LONG_PADDING_V,
  MEAL_SNACK_TAG_OVERLAY_BG,
  MEAL_SNACK_TAG_SHORT_SIZE,
  estimateMealSnackTagLongWidth,
  MENU_FLOW_ITEM_CARD_HEIGHT,
  MENU_FLOW_ITEM_CARD_MARGIN_VERTICAL,
  MENU_FLOW_ITEM_CARD_BORDER_WIDTH,
  MENU_FLOW_ITEM_CARD_INSET,
  MENU_FLOW_ITEM_IMAGE_WIDTH,
  MENU_FLOW_ITEM_IMAGE_SECTION_WIDTH,
  MENU_FLOW_ITEM_CONTENT_PADDING_H,
  MENU_FLOW_ITEM_CONTENT_PADDING_V,
  MENU_FLOW_ITEM_DESCRIPTION_MARGIN_TOP,
  MENU_FLOW_ITEM_BOTTOM_ROW_PADDING_TOP,
  MENU_FLOW_ITEM_BOTTOM_ROW_MIN_HEIGHT,
  MENU_FLOW_ITEM_MACRO_GAP,
  MENU_FLOW_ITEM_STEPPER_BTN_SIZE,
  MENU_FLOW_ITEM_STEPPER_HEIGHT,
  MENU_FLOW_ITEM_STEPPER_VALUE_WIDTH,
  MENU_FLOW_ITEM_STEPPER_WIDTH,
  MENU_FLOW_ITEM_TITLE_MIN_HEIGHT,
  MENU_FLOW_ITEM_IMAGE_BORDER_RADIUS,
  MENU_FLOW_ITEM_BADGE_INSET,
  MENU_FLOW_ITEM_TITLE_FONT_SIZE,
  MENU_FLOW_ITEM_DESC_FONT_SIZE,
  parseMacro,
  getHealthCodeFlags,
  applyQuantityIntent,
  type QuantityIntentContext,
  MENU_ITEM_CARD_BORDER_RADIUS,
  MENU_ITEM_CARD_BORDER_RADIUS_SELECTED,
  MENU_FLOW_ITEM_IMAGE_BORDER_RADIUS_SELECTED,
} from './menuFlowItemCardShared';

export * from './menuFlowItemCardShared';

export interface MenuFlowItemCardProps {
  themeStyleKey: string;
  item: MenuItemData;
  menuBuilderId?: number;
  onQuantityChange: (menu_builder_id: number, itemId: number, isPlusClick: boolean) => void;
  mealsCount: number;
  snacksCount: number;
  isForceFixedMenu?: boolean;
  selectionDisabled?: boolean;
  onSelectionDisabledInteraction?: () => void;
  onItemPress?: (item: MenuItemData) => void;
  isGroupLimitReached?: boolean;
  onLimitExceeded?: (message: string) => void;
  isVariableCarbs?: boolean;
  defaultScaledCarbs?: number;
  maxScaledCarbs?: number;
  onScaledCarbsChange?: (menu_builder_id: number, itemId: number, value: number) => void;
  reduceMotion?: boolean;
}

/** Soft settle — intentionally low bounce. */
const SELECT_SPRING = { stiffness: 380, damping: 42, mass: 0.65 };
/** Quicker collapse on deselect — no bounce. */
const DESELECT_SPRING = { stiffness: 440, damping: 44, mass: 0.55 };
/** Stepper / carbs / qty digit — snappy but with a little life. */
const CONTROL_SPRING = { stiffness: 420, damping: 30, mass: 0.42 };
const CARD_TIMING = { duration: 160, easing: Easing.out(Easing.cubic) };
const INTERACTION_COOLDOWN_MS = 95;
const STEP_HIT_SLOP = { top: 12, bottom: 12, left: 12, right: 12 };
const IMAGE_W = MENU_FLOW_ITEM_IMAGE_WIDTH;
const TRANSPARENT = 'rgba(0,0,0,0)';
/** Select settle — keep near 1 so motion stays subtle. */
const SELECT_SETTLE_DIP = 0.996;
const QTY_POP_FROM = 0.88;
const QTY_POP_PEAK = 1.1;
const SHAKE_PX = 2.5;
const SHAKE_SCALE_PEAK = 1.008;

const AnimatedView = Animated.createAnimatedComponent(View);
const AnimatedImage = Animated.createAnimatedComponent(Image);
const AnimatedText = Animated.createAnimatedComponent(Text);
function createStyles(c: ThemeColors) {
  const android_text = Platform.OS === 'android' ? { includeFontPadding: false as const } : null;
  return StyleSheet.create({
    shell: {
      height: MENU_FLOW_ITEM_CARD_HEIGHT,
      minHeight: MENU_FLOW_ITEM_CARD_HEIGHT,
      marginVertical: MENU_FLOW_ITEM_CARD_MARGIN_VERTICAL,
      width: '100%',
      flexGrow: 0,
      flexShrink: 0,
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'stretch',
      overflow: 'hidden',
      backgroundColor: c.surface,
      borderRadius: DASH_STUDIO_RADIUS,
      borderWidth: MENU_FLOW_ITEM_CARD_BORDER_WIDTH,
      borderColor: 'transparent',
      ...horizontalLtrDirectionStyle,
    },
    row: {
      ...StyleSheet.absoluteFillObject,
      flexDirection: 'row',
      alignItems: 'stretch',
      padding: MENU_FLOW_ITEM_CARD_INSET,
      gap: MENU_FLOW_ITEM_CARD_INSET,
      zIndex: 1,
      ...horizontalLtrDirectionStyle,
    },
    cardHit: {
      ...StyleSheet.absoluteFillObject,
      zIndex: 0,
    },
    cardHitFill: {
      flex: 1,
    },
    imageSection: {
      width: MENU_FLOW_ITEM_IMAGE_SECTION_WIDTH,
      alignSelf: 'center',
      justifyContent: 'center',
      position: 'relative',
      zIndex: 2,
    },
    imageInner: {
      width: IMAGE_W,
      height: IMAGE_W,
      flexShrink: 0,
      overflow: 'hidden',
      backgroundColor: c.surfaceElevated,
    },
    image: {
      width: IMAGE_W,
      height: IMAGE_W,
      overflow: 'hidden',
    },
    expandBtnSection: {
      position: 'absolute',
      bottom: 10,
      left: 10,
      width: MEAL_SNACK_TAG_EXPAND_BTN_SIZE,
      height: MEAL_SNACK_TAG_EXPAND_BTN_SIZE,
      zIndex: 5,
      borderRadius: MEAL_SNACK_TAG_EXPAND_BTN_RADIUS,
      backgroundColor: c.surface,
      overflow: 'hidden',
      opacity: 0.4,
    },
    expandBtn: {
      zIndex: 4,
      justifyContent: 'center',
      alignItems: 'center',
      width: MEAL_SNACK_TAG_EXPAND_BTN_SIZE,
      height: MEAL_SNACK_TAG_EXPAND_BTN_SIZE,
    },
    badgeOverlay: {
      position: 'absolute',
      top: MENU_FLOW_ITEM_BADGE_INSET,
      left: MENU_FLOW_ITEM_BADGE_INSET,
      right: MENU_FLOW_ITEM_BADGE_INSET,
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 6,
    },
    badgeRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6,
      flexShrink: 0,
      maxWidth: '70%',
    },
    tag: {
      backgroundColor: MEAL_SNACK_TAG_OVERLAY_BG,
      borderRadius: MEAL_SNACK_TAG_BORDER_RADIUS,
      position: 'relative',
      overflow: 'hidden',
      justifyContent: 'center',
      alignItems: 'center',
    },
    tagLayer: {
      ...StyleSheet.absoluteFillObject,
      justifyContent: 'center',
      alignItems: 'center',
    },
    healthBadge: {
      backgroundColor: c.error,
      width: 16,
      height: 16,
      borderRadius: 10,
      alignItems: 'center',
      justifyContent: 'center',
    },
    healthBadgeText: { color: c.onError, fontSize: 12, fontWeight: '700' },
    selectionTag: {
      backgroundColor: c.primary,
      height: MEAL_SNACK_TAG_LONG_HEIGHT,
      paddingHorizontal: MEAL_SNACK_TAG_LONG_PADDING_H,
      paddingVertical: MEAL_SNACK_TAG_LONG_PADDING_V,
    },
    tagText: {
      color: c.onPrimary,
      fontSize: 9,
      fontWeight: '800',
      textTransform: 'uppercase',
      textAlign: 'center',
    },
    tagTextLong: {
      color: c.onPrimary,
      fontSize: 9,
      fontWeight: '800',
      textAlign: 'center',
      maxWidth: 120,
    },
    content: {
      flex: 1,
      minWidth: 0,
      justifyContent: 'space-between',
      overflow: 'hidden',
    },
    topRow: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      flexShrink: 0,
      gap: 6,
      position: 'relative',
      ...horizontalLtrDirectionStyle,
    },
    /** Title only — CONTENT_PADDING_H/V apply here, not to the carbs corner control. */
    titleCell: {
      flex: 1,
      minWidth: 0,
      paddingTop: MENU_FLOW_ITEM_CONTENT_PADDING_V,
      paddingHorizontal: MENU_FLOW_ITEM_CONTENT_PADDING_H,
    },
    title: {
      width: '100%',
      fontSize: MENU_FLOW_ITEM_TITLE_FONT_SIZE,
      fontWeight: '600',
      color: c.text,
      minHeight: MENU_FLOW_ITEM_TITLE_MIN_HEIGHT,
    },
    /** Trailing top corner — absolute so hide/show does not reflow the title. */
    carbsCol: {
      position: 'absolute',
      top: 0,
      right: 0,
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      width: MENU_FLOW_ITEM_STEPPER_WIDTH,
      height: MENU_FLOW_ITEM_STEPPER_HEIGHT,
      zIndex: 3,
    },
    carbsBtnWrap: {
      width: MENU_FLOW_ITEM_STEPPER_BTN_SIZE,
      height: MENU_FLOW_ITEM_STEPPER_BTN_SIZE,
      alignItems: 'center',
      justifyContent: 'center',
    },
    carbsValue: {
      width: MENU_FLOW_ITEM_STEPPER_VALUE_WIDTH,
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    },
    carbsAmount: {
      flexShrink: 0,
      fontSize: 10,
      fontWeight: '600',
      color: c.text,
      textAlign: 'center',
      ...android_text,
    },
    carbsTitle: { fontSize: 8, fontWeight: '400', color: c.text, textAlign: 'center', ...android_text },
    carbsBtn: {
      width: MENU_FLOW_ITEM_STEPPER_BTN_SIZE,
      height: MENU_FLOW_ITEM_STEPPER_BTN_SIZE,
      borderRadius: MENU_FLOW_ITEM_STEPPER_BTN_SIZE / 2,
      backgroundColor: c.primary,
      justifyContent: 'center',
      alignItems: 'center',
      overflow: 'hidden',
      flexShrink: 0,
    },
    carbsBtnInner: { width: '100%', height: '100%', justifyContent: 'center', alignItems: 'center' },
    carbsBtnText: {
      color: c.onPrimary,
      fontSize: 12,
      fontWeight: '700',
      lineHeight: 13,
      textAlign: 'center',
      ...(Platform.OS === 'android' ? { includeFontPadding: false, textAlignVertical: 'center' as const } : null),
    },
    /** Description only — CONTENT_PADDING_H/V. */
    textArea: {
      flex: 1,
      minWidth: 0,
      minHeight: 0,
      overflow: 'hidden',
      paddingHorizontal: MENU_FLOW_ITEM_CONTENT_PADDING_H,
      paddingBottom: MENU_FLOW_ITEM_CONTENT_PADDING_V,
    },
    description: {
      width: '100%',
      fontSize: MENU_FLOW_ITEM_DESC_FONT_SIZE,
      color: c.textSecondary,
      marginTop: MENU_FLOW_ITEM_DESCRIPTION_MARGIN_TOP,
      paddingHorizontal: 4,
      flexShrink: 1,
    },
    bottomRow: {
      flexDirection: 'row',
      alignItems: 'flex-end',
      justifyContent: 'space-between',
      paddingTop: MENU_FLOW_ITEM_BOTTOM_ROW_PADDING_TOP,
      minHeight: MENU_FLOW_ITEM_BOTTOM_ROW_MIN_HEIGHT,
      flexShrink: 0,
      width: '100%',
      ...horizontalLtrDirectionStyle,
    },
    bottomRight: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'flex-end',
      flexShrink: 0,
      marginLeft: 6,
    },
    qtyGroup: {
      height: MENU_FLOW_ITEM_STEPPER_HEIGHT,
      justifyContent: 'center',
      alignItems: 'center',
      maxWidth: '100%',
      zIndex: 3,
    },
    macros: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: MENU_FLOW_ITEM_MACRO_GAP,
      flex: 1,
      minWidth: 0,
      paddingHorizontal: MENU_FLOW_ITEM_CONTENT_PADDING_H,
      ...horizontalLtrDirectionStyle,
    },
    macroPoint: { alignItems: 'flex-start' },
    dot: { width: 12, height: 2, borderRadius: 1, marginBottom: 2 },
    macroValue: { fontSize: 13, fontWeight: '700', color: c.text },
    macroLabel: { fontSize: 10, color: c.textLight, paddingHorizontal: 2 },
    qtyCounter: {
      position: 'relative',
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: MENU_FLOW_ITEM_STEPPER_HEIGHT / 2,
      width: MENU_FLOW_ITEM_STEPPER_WIDTH,
      height: MENU_FLOW_ITEM_STEPPER_HEIGHT,
    },
    qtySide: {
      zIndex: 2,
      width: MENU_FLOW_ITEM_STEPPER_BTN_SIZE,
      height: MENU_FLOW_ITEM_STEPPER_BTN_SIZE,
      alignItems: 'center',
      justifyContent: 'center',
    },
    qtyBg: {
      position: 'absolute',
      left: 0,
      top: 0,
      bottom: 0,
      right: 0,
      backgroundColor: c.primary,
      borderRadius: MENU_FLOW_ITEM_STEPPER_HEIGHT / 2,
      height: MENU_FLOW_ITEM_STEPPER_HEIGHT,
      zIndex: 1,
    },
    qtyItem: {
      color: c.onPrimary,
      fontSize: 14,
      fontWeight: '800',
      textAlign: 'center',
      zIndex: 2,
      width: MENU_FLOW_ITEM_STEPPER_VALUE_WIDTH,
    },
  });
}

type Styles = ReturnType<typeof createStyles>;

function MacroPoint({ value, label, color, styles }: { value: number; label: string; color: string; styles: Styles }) {
  return (
    <View style={styles.macroPoint}>
      <View style={[styles.dot, { backgroundColor: color }]} />
      <Text style={styles.macroValue}>
        {formatNumber(value)}
        <Text style={styles.macroLabel}>{label}</Text>
      </Text>
    </View>
  );
}

/** Local press scale for stepper / carbs / expand — uses Clickable2 UI-thread `pressed`. */
function PressPop({
  pressed,
  reduce_motion_sv,
  style,
  children,
}: {
  pressed: SharedValue<number>;
  reduce_motion_sv: SharedValue<number>;
  style?: object;
  children: React.ReactNode;
}) {
  const anim = useAnimatedStyle(() => {
    if (reduce_motion_sv.value > 0.5) {
      return { transform: [{ scale: 1 }], opacity: 1 };
    }
    const p = pressed.value;
    return {
      transform: [{ scale: interpolate(p, [0, 1], [1, 0.9], Extrapolation.CLAMP) }],
      opacity: interpolate(p, [0, 1], [1, 0.92], Extrapolation.CLAMP),
    };
  });
  return <AnimatedView style={[style, anim]}>{children}</AnimatedView>;
}

function ExpandPressChrome({
  pressed,
  reduce_motion_sv,
  style,
  children,
}: {
  pressed: SharedValue<number>;
  reduce_motion_sv: SharedValue<number>;
  style?: object;
  children: React.ReactNode;
}) {
  const anim = useAnimatedStyle(() => {
    if (reduce_motion_sv.value > 0.5) {
      return { transform: [{ scale: 1 }] };
    }
    const p = pressed.value;
    return {
      transform: [{ scale: interpolate(p, [0, 1], [1, 0.97], Extrapolation.CLAMP) }],
    };
  });
  return <AnimatedView style={[style, anim]}>{children}</AnimatedView>;
}

function CardPressShell({
  style,
  onLayout,
  onTouchStart,
  pointerEvents,
  children,
}: {
  style?: object;
  onLayout?: (e: { nativeEvent: { layout: { width: number } } }) => void;
  onTouchStart?: (e: { nativeEvent: { locationX: number } }) => void;
  pointerEvents?: 'auto' | 'none' | 'box-none' | 'box-only';
  children?: React.ReactNode;
}) {
  return (
    <View
      style={style}
      pointerEvents={pointerEvents}
      onLayout={onLayout as never}
      onTouchStart={onTouchStart as never}>
      {children}
    </View>
  );
}

function propsEqual(a: MenuFlowItemCardProps, b: MenuFlowItemCardProps): boolean {
  return (
    a.themeStyleKey === b.themeStyleKey &&
    a.menuBuilderId === b.menuBuilderId &&
    a.item.id === b.item.id &&
    a.item.quantity === b.item.quantity &&
    a.item.is_selected === b.item.is_selected &&
    a.item.selection_number === b.item.selection_number &&
    (a.item as { scaled_carbs?: number }).scaled_carbs === (b.item as { scaled_carbs?: number }).scaled_carbs &&
    (a.item as { health_code?: number }).health_code === (b.item as { health_code?: number }).health_code &&
    a.selectionDisabled === b.selectionDisabled &&
    a.isForceFixedMenu === b.isForceFixedMenu &&
    a.isGroupLimitReached === b.isGroupLimitReached &&
    a.mealsCount === b.mealsCount &&
    a.snacksCount === b.snacksCount &&
    a.isVariableCarbs === b.isVariableCarbs &&
    a.defaultScaledCarbs === b.defaultScaledCarbs &&
    a.maxScaledCarbs === b.maxScaledCarbs &&
    a.reduceMotion === b.reduceMotion
  );
}

export const MenuFlowItemCard: React.FC<MenuFlowItemCardProps> = memo(function MenuFlowItemCard({
  themeStyleKey,
  item,
  menuBuilderId: menuBuilderIdProp,
  onQuantityChange,
  mealsCount,
  snacksCount,
  isForceFixedMenu = false,
  selectionDisabled = false,
  onSelectionDisabledInteraction,
  onItemPress,
  isGroupLimitReached,
  onLimitExceeded,
  isVariableCarbs = false,
  defaultScaledCarbs = 50,
  maxScaledCarbs = 100,
  onScaledCarbsChange,
  reduceMotion = false,
}) {
  const navigation = useAuthNavigation();
  const { t } = useTranslation();
  const nutrition_ui = useNutritionUiEnabled();
  const rtl = useRtl();
  const { colors: themeColors } = useScreenTheme();
  const c = themeColors ?? appTheme.colors;
  const styles = useMemo(() => createStyles(c), [themeStyleKey]);

  const menu_builder_id =
    typeof menuBuilderIdProp === 'number' ? Number(menuBuilderIdProp) : Number(item.menu_builder_id ?? item.food_type_id ?? 0);
  const is_selected = item.is_selected || item.quantity > 0;
  const { isAllergy, isDislike, showBadge } = getHealthCodeFlags(item);
  const is_meal = item.food_type_category?.toLowerCase() === 'meal';
  const limit_message = is_meal ? t('menu.maxMealLimitReached') : t('menu.maxSnackLimitReached');
  const item_name = getApiText(item as unknown as Record<string, unknown>, 'name', 'a_name') || item.name;
  const item_desc =
    getApiText(item as unknown as Record<string, unknown>, 'description', 'a_description') ||
    item.description ||
    t('menu.freshlyPrepared');
  const meal_long = is_meal ? t('menu.meal') : t('menu.snack');
  const tag_long_w = estimateMealSnackTagLongWidth(meal_long);
  const selection_n = Number(item.selection_number ?? 0);
  const show_selection_tag = is_meal && selection_n > 0;
  const selection_label = `#${selection_n}`;
  const selection_tag_w = estimateMealSnackTagLongWidth(selection_label);
  const show_carbs =
    nutrition_ui && isVariableCarbs && !!onScaledCarbsChange && !selectionDisabled;
  const scaled_carbs = resolveMenuItemScaledCarbsDisplay(item, defaultScaledCarbs);

  const active = useSharedValue(is_selected ? 1 : 0);
  const allergy_sv = useSharedValue(isAllergy ? 1 : 0);
  const dislike_sv = useSharedValue(isDislike ? 1 : 0);
  const reduce_motion_sv = useSharedValue(reduceMotion ? 1 : 0);
  const color_primary = useSharedValue(c.primary);
  const color_error = useSharedValue(c.error);
  const color_warning = useSharedValue(c.warning);
  const color_surface = useSharedValue(c.surface);
  const color_surface_elevated = useSharedValue(c.surfaceElevated);
  const shake_x = useSharedValue(0);
  const shake_scale = useSharedValue(1);
  const limit_pulse = useSharedValue(0);
  const qty_pop = useSharedValue(1);
  const carb_pop = useSharedValue(1);
  const tag_w = useSharedValue(tag_long_w);
  const did_sync = useRef(false);
  const did_qty = useRef(false);
  const did_carbs = useRef(false);
  const prev_qty = useRef(item.quantity);
  const prev_carbs = useRef(scaled_carbs);
  const pending_active_target = useRef<0 | 1 | null>(null);
  const full_w = useRef(0);
  const content_w = useRef(200);
  const touch_x = useRef(0);
  const lock = useRef(false);
  /** Keeps qty chrome mounted from the first select frame so reveal can play with `active`. */
  const [display_qty, set_display_qty] = useState(item.quantity);

  const run_guarded = useCallback((fn: () => void) => {
    if (lock.current) {
      return;
    }
    lock.current = true;
    fn();
    setTimeout(() => {
      lock.current = false;
    }, INTERACTION_COOLDOWN_MS);
  }, []);

  const begin_active_to = useCallback(
    (to: 0 | 1) => {
      pending_active_target.current = to;
      cancelAnimation(active);
      if (reduceMotion) {
        active.value = withTiming(to, CARD_TIMING);
        return;
      }
      active.value = withSpring(to, to ? SELECT_SPRING : DESELECT_SPRING);
    },
    [reduceMotion],
  );

  const run_digit_pop = useCallback(
    (sv: SharedValue<number>) => {
      if (reduceMotion) {
        sv.value = 1;
        return;
      }
      cancelAnimation(sv);
      sv.value = QTY_POP_FROM;
      sv.value = withSequence(withSpring(QTY_POP_PEAK, CONTROL_SPRING), withSpring(1, CONTROL_SPRING));
    },
    [reduceMotion],
  );

  useLayoutEffect(() => {
    tag_w.value = tag_long_w;
  }, [tag_long_w]);

  useLayoutEffect(() => {
    allergy_sv.value = isAllergy ? 1 : 0;
    dislike_sv.value = isDislike ? 1 : 0;
  }, [isAllergy, isDislike]);

  useLayoutEffect(() => {
    reduce_motion_sv.value = reduceMotion ? 1 : 0;
  }, [reduceMotion]);

  useLayoutEffect(() => {
    color_primary.value = c.primary;
    color_error.value = c.error;
    color_warning.value = c.warning;
    color_surface.value = c.surface;
    color_surface_elevated.value = c.surfaceElevated;
  }, [themeStyleKey]);

  useLayoutEffect(() => {
    if (!did_sync.current) {
      did_sync.current = true;
      return;
    }
    const target: 0 | 1 = is_selected ? 1 : 0;
    if (pending_active_target.current === target) {
      pending_active_target.current = null;
      return;
    }
    pending_active_target.current = null;
    cancelAnimation(active);
    if (reduceMotion) {
      active.value = withTiming(target, CARD_TIMING);
      return;
    }
    active.value = withSpring(target, target ? SELECT_SPRING : DESELECT_SPRING);
  }, [is_selected, reduceMotion]);

  useLayoutEffect(() => {
    if (!did_qty.current) {
      did_qty.current = true;
      prev_qty.current = item.quantity;
      set_display_qty(item.quantity);
      return;
    }
    if (prev_qty.current === item.quantity) {
      return;
    }
    prev_qty.current = item.quantity;
    if (item.quantity < 1) {
      qty_pop.value = 1;
      // Hold chrome briefly so deselect reveal can finish with `active`.
      const hold_ms = reduceMotion ? 0 : 260;
      const timer = setTimeout(() => {
        set_display_qty(0);
      }, hold_ms);
      return () => clearTimeout(timer);
    }
    set_display_qty(item.quantity);
    run_digit_pop(qty_pop);
  }, [item.quantity, reduceMotion, run_digit_pop]);

  useLayoutEffect(() => {
    if (!did_carbs.current) {
      did_carbs.current = true;
      prev_carbs.current = scaled_carbs;
      return;
    }
    if (prev_carbs.current === scaled_carbs) {
      return;
    }
    prev_carbs.current = scaled_carbs;
    run_digit_pop(carb_pop);
  }, [scaled_carbs, run_digit_pop]);

  const trigger_shake = useCallback(
    (direction: 'left' | 'right') => {
      const health: MenuItemHealthHaptic = isAllergy ? 'allergy' : isDislike ? 'dislike' : 'none';
      triggerMenuItemHaptic('shake', health);
      cancelAnimation(shake_x);
      cancelAnimation(shake_scale);
      cancelAnimation(limit_pulse);
      if (reduceMotion) {
        limit_pulse.value = withSequence(
          withTiming(1, { duration: 70, easing: Easing.out(Easing.cubic) }),
          withTiming(0, { duration: 160, easing: Easing.inOut(Easing.ease) }),
        );
        return;
      }
      const sign = direction === 'left' ? -1 : 1;
      shake_x.value = withSequence(
        withTiming(sign * SHAKE_PX, { duration: 40, easing: Easing.out(Easing.cubic) }),
        withTiming(sign * -SHAKE_PX * 0.7, { duration: 45, easing: Easing.inOut(Easing.ease) }),
        withTiming(sign * SHAKE_PX * 0.35, { duration: 40, easing: Easing.inOut(Easing.ease) }),
        withTiming(0, { duration: 55, easing: Easing.out(Easing.cubic) }),
      );
      shake_scale.value = withSequence(
        withTiming(SHAKE_SCALE_PEAK, { duration: 45, easing: Easing.out(Easing.cubic) }),
        withTiming(1, { duration: 120, easing: Easing.out(Easing.cubic) }),
      );
      limit_pulse.value = withSequence(
        withTiming(1, { duration: 70, easing: Easing.out(Easing.cubic) }),
        withTiming(0, { duration: 200, easing: Easing.inOut(Easing.ease) }),
      );
    },
    [isAllergy, isDislike, reduceMotion],
  );

  const qty_ctx = useCallback((): QuantityIntentContext => {
    const limit = is_meal ? mealsCount : snacksCount;
    return {
      quantity: item.quantity,
      isForceFixedMenu,
      selectionDisabled,
      wouldExceedLimit: item.quantity >= limit || isGroupLimitReached === true,
      menuBuilderId: menu_builder_id,
      itemId: item.id,
      limitMessage: limit_message,
      isAllergy,
      isDislike,
      onQuantityChange,
      onSelectionDisabledInteraction,
      onLimitExceeded,
      triggerShake: trigger_shake,
      onCardQuantityZeroLimit: () => {
        begin_active_to(0);
      },
    };
  }, [
    item.quantity,
    item.id,
    isForceFixedMenu,
    selectionDisabled,
    is_meal,
    mealsCount,
    snacksCount,
    isGroupLimitReached,
    menu_builder_id,
    limit_message,
    isAllergy,
    isDislike,
    onQuantityChange,
    onSelectionDisabledInteraction,
    onLimitExceeded,
    trigger_shake,
    begin_active_to,
  ]);

  const open_detail = useCallback(() => {
    run_guarded(() => {
      triggerTactileHaptic('press');
      if (onItemPress) {
        onItemPress(item);
      } else {
        navigation.navigate('FoodItemDetail', { item });
      }
    });
  }, [item, onItemPress, navigation, run_guarded]);

  const maybe_optimistic_select = useCallback(
    (intent: 'increment' | 'decrement', source: 'card' | 'stepper') => {
      if (selectionDisabled || isForceFixedMenu) {
        return;
      }
      const health_code =
        typeof (item as { health_code?: number }).health_code === 'number'
          ? (item as { health_code: number }).health_code
          : 0;
      const has_customizations =
        (item.is_customizations_available ?? 0) === 1 &&
        Array.isArray(item.customizations) &&
        item.customizations.length > 0;
      const limit = is_meal ? mealsCount : snacksCount;
      const would_exceed = item.quantity >= limit || isGroupLimitReached === true;

      if (intent === 'decrement') {
        if (item.quantity < 1) {
          return;
        }
        if (item.quantity === 1) {
          begin_active_to(0);
          // Keep qty chrome mounted until store qty hits 0 so exit can play with `active`.
          return;
        }
        set_display_qty(item.quantity - 1);
        return;
      }

      if (source === 'stepper') {
        if (would_exceed) {
          return;
        }
        if (item.quantity === 0 && (health_code !== 0 || has_customizations)) {
          return;
        }
        set_display_qty(item.quantity + 1);
        if (item.quantity === 0) {
          begin_active_to(1);
        }
        return;
      }

      // card toggle
      if (item.quantity === 0) {
        if (would_exceed || health_code !== 0 || has_customizations) {
          return;
        }
        set_display_qty(1);
        begin_active_to(1);
        return;
      }
      if (item.quantity === 1) {
        begin_active_to(0);
      }
    },
    [
      selectionDisabled,
      isForceFixedMenu,
      item,
      is_meal,
      mealsCount,
      snacksCount,
      isGroupLimitReached,
      begin_active_to,
    ],
  );

  const on_card_press = useCallback(() => {
    run_guarded(() => {
      const { direction } = resolveMenuFlowCardTap(touch_x.current, full_w.current, content_w.current, rtl.isRTL);
      maybe_optimistic_select('increment', 'card');
      applyQuantityIntent('increment', 'card', direction, qty_ctx());
    });
  }, [qty_ctx, run_guarded, rtl.isRTL, maybe_optimistic_select]);

  const on_plus = useCallback(() => {
    run_guarded(() => {
      maybe_optimistic_select('increment', 'stepper');
      applyQuantityIntent('increment', 'stepper', 'right', qty_ctx());
    });
  }, [qty_ctx, run_guarded, maybe_optimistic_select]);

  const on_minus = useCallback(() => {
    run_guarded(() => {
      maybe_optimistic_select('decrement', 'stepper');
      applyQuantityIntent('decrement', 'stepper', 'left', qty_ctx());
    });
  }, [qty_ctx, run_guarded, maybe_optimistic_select]);

  const bump_carbs = useCallback(
    (delta: number) => {
      run_guarded(() => {
        if (selectionDisabled) {
          onSelectionDisabledInteraction?.();
          return;
        }
        if (!onScaledCarbsChange) {
          return;
        }
        triggerTactileHaptic('press');
        const next = Math.max(0, Math.min(maxScaledCarbs || 999, scaled_carbs + delta));
        onScaledCarbsChange(menu_builder_id, item.id, next);
      });
    },
    [
      selectionDisabled,
      onSelectionDisabledInteraction,
      onScaledCarbsChange,
      maxScaledCarbs,
      scaled_carbs,
      menu_builder_id,
      item.id,
      run_guarded,
    ],
  );

  const border_color_sv = useDerivedValue(() => {
    const accent =
      allergy_sv.value > 0.5
        ? color_error.value
        : dislike_sv.value > 0.5
          ? color_warning.value
          : color_primary.value;
    const base = interpolateColor(active.value, [0, 1], [TRANSPARENT, accent]);
    return interpolateColor(limit_pulse.value, [0, 1], [base, accent]);
  });

  const chrome_style = useAnimatedStyle(() => {
    const t = active.value;
    const select_settle =
      reduce_motion_sv.value > 0.5
        ? 1
        : interpolate(t, [0, 0.5, 1], [1, SELECT_SETTLE_DIP, 1], Extrapolation.CLAMP);
    return {
      transform: [
        { translateX: shake_x.value },
        { scale: select_settle * shake_scale.value },
      ],
      borderRadius: interpolate(t, [0, 1], [MENU_ITEM_CARD_BORDER_RADIUS, MENU_ITEM_CARD_BORDER_RADIUS_SELECTED]),
      borderWidth: MENU_FLOW_ITEM_CARD_BORDER_WIDTH,
      borderColor: border_color_sv.value,
      backgroundColor: interpolateColor(t, [0, 1], [color_surface.value, color_surface_elevated.value]),
    };
  });

  const image_r = useAnimatedStyle(() => ({
    borderRadius: interpolate(
      active.value,
      [0, 1],
      [MENU_FLOW_ITEM_IMAGE_BORDER_RADIUS, MENU_FLOW_ITEM_IMAGE_BORDER_RADIUS_SELECTED],
    ),
  }));

  const selection_badge = useAnimatedStyle(() => ({
    opacity: interpolate(active.value, [0.5, 1], [0, 1], Extrapolation.CLAMP),
    transform: [{ scale: interpolate(active.value, [0.5, 1], [0.96, 1], Extrapolation.CLAMP) }],
  }));
  const tag_morph = useAnimatedStyle(() => ({
    width: interpolate(active.value, [0, 0.8], [MEAL_SNACK_TAG_SHORT_SIZE, tag_w.value], Extrapolation.CLAMP),
    height: interpolate(active.value, [0, 0.8], [MEAL_SNACK_TAG_SHORT_SIZE, MEAL_SNACK_TAG_LONG_HEIGHT], Extrapolation.CLAMP),
    paddingHorizontal: interpolate(active.value, [0, 0.8], [4, MEAL_SNACK_TAG_LONG_PADDING_H], Extrapolation.CLAMP),
    paddingVertical: interpolate(active.value, [0, 0.8], [4, MEAL_SNACK_TAG_LONG_PADDING_V], Extrapolation.CLAMP),
  }));
  const tag_short = useAnimatedStyle(() => ({
    opacity: interpolate(active.value, [0, 0.5], [1, 0], Extrapolation.CLAMP),
  }));
  const tag_long = useAnimatedStyle(() => ({
    opacity: interpolate(active.value, [0.3, 0.85], [0, 1], Extrapolation.CLAMP),
  }));
  const stepper_fade = useAnimatedStyle(() => ({
    opacity: interpolate(active.value, [0.2, 0.85], [0, 1], Extrapolation.CLAMP),
    transform: [{ scale: interpolate(active.value, [0.2, 0.85], [0.86, 1], Extrapolation.CLAMP) }],
  }));
  /** Pill fill follows select — light scale so it feels grown-in, not stretched. */
  const qty_bg = useAnimatedStyle(() => ({
    opacity: interpolate(active.value, [0.15, 0.75], [0, 1], Extrapolation.CLAMP),
    transform: [{ scale: interpolate(active.value, [0.15, 0.75], [0.9, 1], Extrapolation.CLAMP) }],
  }));
  const qty_digit_anim = useAnimatedStyle(() => ({
    transform: [{ scale: qty_pop.value }],
  }));
  const carbs_btn_pop = useAnimatedStyle(() => ({
    opacity: interpolate(active.value, [0.25, 0.9], [0, 1], Extrapolation.CLAMP),
    transform: [{ scale: interpolate(active.value, [0.25, 0.9], [0.84, 1], Extrapolation.CLAMP) }],
  }));
  const carbs_value_anim = useAnimatedStyle(() => ({
    opacity: interpolate(active.value, [0.3, 0.95], [0, 1], Extrapolation.CLAMP),
    transform: [{ scale: carb_pop.value * interpolate(active.value, [0.3, 0.95], [0.9, 1], Extrapolation.CLAMP) }],
  }));
  const carbs_col_anim = useAnimatedStyle(() => ({
    opacity: interpolate(active.value, [0.2, 0.85], [0, 1], Extrapolation.CLAMP),
    transform: [{ scale: interpolate(active.value, [0.2, 0.85], [0.92, 1], Extrapolation.CLAMP) }],
  }));

  const macros = {
    cal: parseMacro(item.calories),
    prot: parseMacro(item.protein),
    carb: parseMacro(item.carbs),
    fat: parseMacro(item.fats),
  };

  return (
    <AnimatedView style={[styles.shell, chrome_style]}>
      {/*
        Card select sits behind the chrome. Nested Clickable2 gestures cannot reliably
        win over a parent Tap — so expand / qty / carbs are siblings on top with
        pointerEvents, and empty chrome uses box-none / none so taps fall through.
      */}
      <Clickable2
        onPress={on_card_press}
        style={styles.cardHit}
        accessibilityRole="button"
        accessibilityLabel={item_name}
        haptics={false}>
        <CardPressShell
          style={styles.cardHitFill}
          onLayout={e => {
            full_w.current = e.nativeEvent.layout.width;
          }}
          onTouchStart={e => {
            touch_x.current = e.nativeEvent.locationX;
          }}
        />
      </Clickable2>

      <View style={styles.row} pointerEvents="box-none">
        <View style={styles.imageSection} pointerEvents="box-none">
          <AnimatedView style={[styles.imageInner, image_r]} pointerEvents="none">
            <AnimatedImage source={{ uri: item.attachment }} style={[styles.image, image_r]} resizeMode="cover" />
          </AnimatedView>
          <Clickable2
            onPress={open_detail}
            style={styles.expandBtnSection}
            accessibilityRole="button"
            accessibilityLabel={t('menu.openItemDetail')}
            haptics
            hapticType="soft">
            {({ pressed: expand_pressed }: Clickable2PressState) => (
              <ExpandPressChrome pressed={expand_pressed} reduce_motion_sv={reduce_motion_sv} style={styles.expandBtn}>
                <SvgIcon name="expand2" size={MEAL_SNACK_TAG_EXPAND_ICON_SIZE} color={c.text} />
              </ExpandPressChrome>
            )}
          </Clickable2>
          <View style={styles.badgeOverlay} pointerEvents="none">
            <View style={styles.badgeRow}>
              {showBadge ? (
                <View
                  style={[
                    styles.healthBadge,
                    isDislike && !isAllergy ? { backgroundColor: c.warning } : null,
                  ]}>
                  <Text style={styles.healthBadgeText}>!</Text>
                </View>
              ) : null}
              <Animated.View style={[styles.tag, tag_morph]}>
                <Animated.View style={[styles.tagLayer, tag_short]} pointerEvents="none">
                  <Text style={styles.tagText} numberOfLines={1} allowFontScaling={false}>
                    {is_meal ? 'M' : 'S'}
                  </Text>
                </Animated.View>
                <Animated.View style={[styles.tagLayer, tag_long]} pointerEvents="none">
                  <Text
                    style={[styles.tagTextLong, { writingDirection: rtl.writingDirection }]}
                    numberOfLines={1}
                    ellipsizeMode="tail"
                    allowFontScaling={false}>
                    {meal_long}
                  </Text>
                </Animated.View>
              </Animated.View>
              {show_selection_tag ? (
                <Animated.View
                  style={[styles.tag, styles.selectionTag, { width: selection_tag_w }, selection_badge]}>
                  <Text style={styles.tagText} numberOfLines={1} allowFontScaling={false}>
                    {selection_label}
                  </Text>
                </Animated.View>
              ) : null}
            </View>
          </View>
        </View>

        <View style={styles.content} pointerEvents="box-none">
          <View style={{ flex: 1, minWidth: 0, minHeight: 0 }} pointerEvents="box-none">
            <View
              style={styles.topRow}
              pointerEvents="box-none"
              onLayout={e => {
                content_w.current = e.nativeEvent.layout.width;
              }}>
              <View
                style={[
                  styles.titleCell,
                  show_carbs && display_qty >= 1 ? { paddingEnd: MENU_FLOW_ITEM_STEPPER_WIDTH + 6 } : null,
                ]}
                pointerEvents="none">
                <Text style={[styles.title, rtl.leadingMediaCopyTextStyle]} numberOfLines={1} ellipsizeMode="tail">
                  {item_name}
                </Text>
              </View>
              {show_carbs ? (
                <AnimatedView
                  style={[styles.carbsCol, carbs_col_anim, { transformOrigin: 'right' }]}
                  pointerEvents={display_qty >= 1 ? 'box-none' : 'none'}>
                  <AnimatedView style={[styles.carbsBtnWrap, carbs_btn_pop]} pointerEvents="auto">
                    <Clickable2
                      onPressIn={() => bump_carbs(-5)}
                      style={styles.carbsBtn}
                      hitSlop={{ top: 10, bottom: 10, left: 8, right: 4 }}
                      accessibilityLabel={`${t('menu.scaledCarbs')} minus`}
                      accessibilityRole="button"
                      haptics={false}>
                      {({ pressed: carb_pressed }: Clickable2PressState) => (
                        <PressPop pressed={carb_pressed} reduce_motion_sv={reduce_motion_sv} style={styles.carbsBtnInner}>
                          <Text style={styles.carbsBtnText}>−</Text>
                        </PressPop>
                      )}
                    </Clickable2>
                  </AnimatedView>
                  <AnimatedView
                    style={[styles.carbsValue, carbs_value_anim]}
                    pointerEvents="none"
                    accessibilityLabel={`${formatNumber(scaled_carbs)} ${t('menu.scaledCarbs')}`}>
                    <Text style={styles.carbsTitle}>{t('menu.scaledCarbs')}</Text>
                    <Text style={styles.carbsAmount}>{formatNumber(scaled_carbs)}</Text>
                  </AnimatedView>
                  <AnimatedView style={[styles.carbsBtnWrap, carbs_btn_pop]} pointerEvents="auto">
                    <Clickable2
                      onPressIn={() => bump_carbs(5)}
                      style={styles.carbsBtn}
                      hitSlop={{ top: 10, bottom: 10, left: 4, right: 8 }}
                      accessibilityLabel={`${t('menu.scaledCarbs')} plus`}
                      accessibilityRole="button"
                      haptics={false}>
                      {({ pressed: carb_pressed }: Clickable2PressState) => (
                        <PressPop pressed={carb_pressed} reduce_motion_sv={reduce_motion_sv} style={styles.carbsBtnInner}>
                          <Text style={styles.carbsBtnText}>+</Text>
                        </PressPop>
                      )}
                    </Clickable2>
                  </AnimatedView>
                </AnimatedView>
              ) : null}
            </View>
            <View style={styles.textArea} pointerEvents="none">
              <Text style={[styles.description, rtl.leadingMediaCopyTextStyle]} numberOfLines={3}>
                {item_desc}
              </Text>
            </View>
          </View>

          <View style={styles.bottomRow} pointerEvents="box-none">
            {nutrition_ui ? (
              <View style={styles.macros} pointerEvents="none">
                <MacroPoint value={macros.cal} label="Kcal" color={c.calorie} styles={styles} />
                <MacroPoint value={macros.fat} label="F" color={c.fats} styles={styles} />
                <MacroPoint value={macros.prot} label="P" color={c.protein} styles={styles} />
                <MacroPoint value={macros.carb} label="C" color={c.carbs} styles={styles} />
              </View>
            ) : (
              <View style={{ flex: 1 }} pointerEvents="none" />
            )}
            <View style={styles.bottomRight} pointerEvents="box-none">
              {display_qty >= 1 ? (
                <View style={styles.qtyGroup} pointerEvents="box-none">
                  <View style={styles.qtyCounter} pointerEvents="box-none">
                    <AnimatedView style={[styles.qtyBg, qty_bg, { transformOrigin: 'right' }]} pointerEvents="none" />
                    <AnimatedView style={[styles.qtySide, stepper_fade]} pointerEvents="auto">
                      <Clickable2
                        onPressIn={on_minus}
                        hitSlop={STEP_HIT_SLOP}
                        accessibilityRole="button"
                        accessibilityLabel={t('menu.decreaseQuantity')}
                        style={styles.qtySide}
                        haptics={false}>
                        {({ pressed: step_pressed }: Clickable2PressState) => (
                          <PressPop pressed={step_pressed} reduce_motion_sv={reduce_motion_sv}>
                            <Text style={styles.qtyItem}>-</Text>
                          </PressPop>
                        )}
                      </Clickable2>
                    </AnimatedView>
                    <AnimatedText style={[styles.qtyItem, qty_digit_anim]} pointerEvents="none">
                      {formatNumber(display_qty)}
                    </AnimatedText>
                    <AnimatedView style={[styles.qtySide, stepper_fade]} pointerEvents="auto">
                      <Clickable2
                        onPressIn={on_plus}
                        hitSlop={STEP_HIT_SLOP}
                        accessibilityRole="button"
                        accessibilityLabel={t('menu.increaseQuantity')}
                        style={styles.qtySide}
                        haptics={false}>
                        {({ pressed: step_pressed }: Clickable2PressState) => (
                          <PressPop pressed={step_pressed} reduce_motion_sv={reduce_motion_sv}>
                            <Text style={styles.qtyItem}>+</Text>
                          </PressPop>
                        )}
                      </Clickable2>
                    </AnimatedView>
                  </View>
                </View>
              ) : null}
            </View>
          </View>
        </View>
      </View>
    </AnimatedView>
  );
}, propsEqual);
